import { Card, CardActions, Collapse, IconButton, IconButtonProps, List, ListItem, styled, Typography } from '@mui/material';
import React from 'react';
import ExpandIcon from 'remixicon-react/ArrowDownSFillIcon';
import { ConfigContext } from '../../../../App/App';
import { ChatMessage } from '../../../../Shared/API/ApiTypes';
import MessageSending from '../../MessageSender/MessageSending';
import StructuredDataResultsList from '../StructuredData/StructuredDataResultsList';
import './Message.css';
import MessageContent from './MessageContent';

interface ExpandMoreProps extends IconButtonProps {
  expand: boolean;
}

const ExpandMore = styled((props: ExpandMoreProps) => {
  const { expand, ...other } = props;
  return <IconButton {...other} sx={{ p: 0, display: 'flex', justifyContent: 'center' }} />;
})(({ theme, expand }) => ({
  transform: !expand ? 'rotate(0deg)' : 'rotate(180deg)',
  marginLeft: 'auto',
  transition: theme.transitions.create('transform', {
    duration: theme.transitions.duration.shortest,
  }),
}));

type MessageProps = {
  id?: string;
  message: ChatMessage;
  tabIndex?: number;
};

const Message = React.forwardRef<HTMLDivElement, MessageProps>(({ id, message, tabIndex = -1 }, ref) => {
  const config = React.useContext(ConfigContext);
  const [expanded, setExpanded] = React.useState(false);
  const botName = config?.BotName ?? 'bot';

  const handleExpandClick = () => {
    setExpanded(!expanded);
  };

  return (
    <div
      className={'messageRow ' + (message.direction === 0 ? 'zero' : 'one')}
      data-testid="chat-message-row"
      id={id}
      ref={ref}
      tabIndex={tabIndex}
    >
      <Card elevation={2} className="chatMessage"
        sx={{
          p: 1,
          background: message.direction === 0 ? `${config.PrimaryColor}` : `white`,
          borderRadius: 2,
          color: message.direction === 0 ? 'white' : 'black',
          '&:hover': {
            backgroundColor: message.direction === 0 ? 'primary.light' : 'white'
          }
        }}
      >
        <div className="messageInfo">
          {message.direction === 0 ? 'You' : botName}
          <div>
            |
          </div>
          <div>
            {new Date(message.time).toLocaleString([], { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' })}
          </div>
        </div>
        <div>
          {message.temp && message.content === "" &&
            <MessageSending />
          }
          <MessageContent messageContent={message.content} />
        </div>
        {message.direction === 1 && message.relatedLinks &&
          <>
            <CardActions disableSpacing sx={{ ml: 0, p: 0, pt: 1 }}
              onClick={handleExpandClick}>
              <Typography color="GrayText" variant='body2' sx={{ fontWeight: 'bold' }}>Related Links</Typography>
              <ExpandMore
                expand={expanded}
                aria-expanded={expanded}
                aria-label="Related Links"
              >
                <ExpandIcon />
              </ExpandMore>
              {/* <Button variant="text" size="small" sx={{ background: 'white', backgroundColor: 'white !important', boxShadow: 'none', p: 0, m: 0 }}>Related Links</Button> */}
            </CardActions>
            <Collapse in={expanded} timeout="auto" unmountOnExit>
              <List sx={{ fontSize: '.8rem' }}>
                {message.relatedLinks.map((l, i) => {
                  return <ListItem key={i} sx={{ borderLeft: `3px solid ${config.SecondaryColor}`, marginBottom: 1, color: config.PrimaryColor }}><a href={l.url} target="_blank" rel="noreferrer">{l.title}</a></ListItem>
                })}

              </List>
            </Collapse>
          </>
        }
      </Card>
      {message.direction === 1 && message.cards &&
        <StructuredDataResultsList cards={message.cards} startCollapsed={true} />
      }
    </div>
  );
});

Message.displayName = 'Message';

export default Message;
